import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import { TEXTAREA_CLS } from "@/lib/analysis";
import { type AnalysisStreamFailure, streamAnalysis } from "@/lib/analysis-stream";
import { api } from "@/lib/api";
import { useEffect, useState } from "react";

/** The same failures, worded for a pasted transcript. */
function analyzeError(f: AnalysisStreamFailure): string {
  switch (f.reason) {
    case "http":
      if (f.status === 503) {
        return (
          f.detail ??
          "AI is not configured on the server. Set up the LiteLLM gateway and try again."
        );
      }
      if (f.status === 400) return f.detail ?? "There was nothing to analyse in this transcript.";
      return f.detail ? `Couldn't analyse: ${f.detail}` : "Couldn't analyse the transcript.";
    case "stream":
      return `The AI analysis failed: ${f.detail ?? "unknown error"}`;
    case "incomplete":
    case "interrupted":
      return "Lost the connection while the analysis was running. It may have finished anyway — check the Meetings list before analysing it again.";
    case "timeout":
      return "Couldn't analyse the transcript (it timed out).";
    default:
      return "Couldn't analyse the transcript (no response from the server).";
  }
}

/**
 * The one **manually-run** analysis source: a pasted meeting transcript.
 * Lives at the top of the AI Suggestions page (collapsed by default). Running
 * it saves the proposals as pending suggestions; the parent then refreshes
 * the queue below, where the admin approves/rejects them — so there are no
 * separate inline cards here, just the run + a short confirmation.
 */

type AnalyzeResult = {
  analysisId: string | null;
  summary: string;
  suggestions: Array<{ id: string }>;
  /** Proposed time entries — a separate entity with its own queue, but part of
   *  the same run, so the "added N proposals" count has to include them. */
  timeSuggestions: Array<{ id: string }>;
};

export function TranscriptAnalyzer({
  onAnalyzed,
  defaultOpen = false,
}: {
  onAnalyzed: () => void;
  /** Skip the collapsed "+ Analyse…" button and show the form straight away
   *  (e.g. when rendered inside a modal). */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [transcript, setTranscript] = useState("");

  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState<{ tokens: number; phase: "thinking" | "writing" }>({
    tokens: 0,
    phase: "thinking",
  });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ summary: string; count: number } | null>(null);

  useEffect(() => {
    if (!open || projects.length) return;
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => setProjects(res.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {
        /* the picker just stays empty; analysis can still run with no project */
      });
  }, [open, projects.length]);

  async function analyze() {
    setError(null);
    setDone(null);
    const text = transcript.trim();
    if (!text) {
      setError("Paste a meeting transcript first.");
      return;
    }

    setAnalyzing(true);
    setProgress({ tokens: 0, phase: "thinking" });

    try {
      const run = await streamAnalysis<AnalyzeResult>(
        "/api/analysis/analyze",
        { transcript: text, projectIds: selectedProjectIds },
        { onProgress: setProgress },
      );

      if (!run.ok) {
        setError(analyzeError(run));
        return;
      }
      const final = run.result;
      setDone({
        summary: final.summary,
        count: final.suggestions.length + (final.timeSuggestions?.length ?? 0),
      });
      setTranscript("");
      onAnalyzed();
    } finally {
      setAnalyzing(false);
    }
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="open-transcript-analyzer"
      >
        + Analyse a meeting transcript
      </Button>
    );
  }

  const projectOptions = projects.map((p) => ({ value: p.id, label: p.name }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analyse a meeting transcript</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <span className="text-sm font-medium leading-none">Projects discussed</span>
          <MultiSelectFilter
            options={projectOptions}
            values={selectedProjectIds}
            exclude={false}
            onChange={setSelectedProjectIds}
            placeholder="All / no specific project"
            noun="projects"
            aria-label="Projects discussed"
          />
          <p className="text-xs text-muted-foreground">
            Pick the project(s) the meeting concerned so the AI can suggest changes to their open
            work items. Leave empty to only get new-item / idea suggestions.
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="transcript" className="text-sm font-medium leading-none">
            Transcript text
          </label>
          <textarea
            id="transcript"
            data-testid="transcript-input"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={10}
            placeholder="Paste the meeting transcript here…"
            className={TEXTAREA_CLS}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {done && (
          <div className="rounded-md bg-muted/50 p-3 text-sm" data-testid="analyze-done">
            <p className="font-medium">
              Added {done.count} suggestion{done.count === 1 ? "" : "s"} to the queue below.
            </p>
            {done.summary && <p className="mt-1 text-muted-foreground">{done.summary}</p>}
          </div>
        )}

        <div className="space-y-3">
          <Button type="button" onClick={analyze} disabled={analyzing} data-testid="analyze-button">
            {analyzing ? "Analysing…" : "Analyse transcript"}
          </Button>
          {analyzing && <AnalyzeProgress tokens={progress.tokens} phase={progress.phase} />}
        </div>
      </CardContent>
    </Card>
  );
}

/** Soft target (output tokens) a typical analysis produces — turns the real
 *  streamed token count into a percentage, capped at 95% until the result lands. */
const PROGRESS_TOKEN_TARGET = 3000;

function AnalyzeProgress({ tokens, phase }: { tokens: number; phase: "thinking" | "writing" }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const pct =
    phase === "thinking"
      ? 6
      : Math.min(95, Math.max(8, Math.round((tokens / PROGRESS_TOKEN_TARGET) * 100)));
  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;

  return (
    <output className="block space-y-1" data-testid="analyze-progress" aria-live="polite">
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Analysing the transcript"
        tabIndex={-1}
      >
        <div
          className={`h-full rounded-full bg-primary transition-[width] duration-300 ease-out ${
            phase === "thinking" ? "animate-pulse" : ""
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {phase === "thinking" ? "Reading the transcript…" : "Writing suggestions…"}
        {phase === "writing" && (
          <span data-testid="analyze-tokens">
            {" · "}
            {tokens.toLocaleString()} tokens
          </span>
        )}
        {" · "}
        {mmss}
      </p>
    </output>
  );
}
